import {
  Button, CircularProgress, Grid, makeStyles,
  Menu, MenuItem, Typography, Box,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { displayChildAge, displayFacilityType, displayFamilyType } from '../../modules/data';
import states from '../../modules/states';
import { toPercent } from '../../modules/text';
import MessageBox from '../MessageBox';
import ResponsiveContainer from '../ResponsiveContainer';
import { getMedianHouseholdIncome, getRatesByState } from './data';

function PercentOfPriceAndPercentOfMedianIncomePoverty() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [pieData, setPieData] = useState([]);
  const [percentValue, setPercentValue] = useState(25);
  const { familyType, settingType, childAge } = useContext(FiltersContext);
  const { primaryColor, contrastColor } = useContext(StoryContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const [error, setError] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(null);
  // const [averageIncomeData, setAverageIncomeData] = useState(null);
  const [averageIncome, setAverageIncome] = useState(0);
  const [payPercent, setPayPercent] = useState(0);

  const classes = makeStyles((theme) => ({

    messageHeader: {
      color: primaryColor,
    },
    button: {
      textTransform: 'capitalize',
      marginRight: theme.spacing(1),
    },
  }))();

  useEffect(() => {
    setReady(false);

    Promise.all([getRatesByState(stateCode, null, { settingType, childAge }), getMedianHouseholdIncome(stateCode)])
      .then((responses) => {
        setMonthlyPrice(responses[0].parsedBody.dataProvider[0].average_rate / 12);
        // setAverageIncomeData(responses[1].parsedBody.dataProvider[0]);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setReady(true);
      });
  }, [settingType, childAge, stateCode]);

  useEffect(() => {
    switch (familyType.toLowerCase()) {
      case 'family of 3':
        setAverageIncome(20578 / 12);
        break;

      case 'family of 2':
        setAverageIncome(17622 / 12);
        break;

      default:
        setAverageIncome(20578 / 12);
        break;
    }
  }, [familyType]);

  useEffect(() => {
    const percent = (monthlyPrice / ((percentValue / 100) * averageIncome)) * 100;
    setPayPercent(percent);
    setPieData([{ name: 'Child Care', value: percent }, { name: 'Other', value: (100 - percent) < 0 ? 0 : (100 - percent) }]);
  }, [averageIncome, monthlyPrice, percentValue]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePercentChange = (event) => {
    setPercentValue(event.target.value);
    handleClose();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  function renderSelectValues() {
    const items = [];
    for (let i = 25; i <= 300; i += 25) {
      items.push(<MenuItem onClick={handlePercentChange} key={i} value={i}>{i}%</MenuItem>);
    }
    return items;
  }

  const renderCustomizedLabel = ({
    cx, cy, percent, index,
  }) => {
    if (index > 0) return null; // only need to show the first value in the center of the donut

    return (
      <text x={cx} y={cy} fontSize="3em" fill={primaryColor} textAnchor="middle" dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <Grid container spacing={2} justify="center">
        <Grid item xs={12} md={8}>
          <MessageBox>
            <Typography variant="body1" align="left">
              In  <b>{states[stateCode.toUpperCase()]}</b>,
              a <b>{displayFamilyType(familyType).toLowerCase()}</b> with one <b>{displayChildAge(childAge).toLowerCase()}</b>, earning <b>{percentValue}%</b> of FPL, pays <b>{toPercent(payPercent / 100)}</b> of
              their household income for <b>{displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b>.
            </Typography>
          </MessageBox>
        </Grid>
        <Grid item xs={6} style={{ textAlign: 'center' }}>
          <Box displayPrint="none">
            <Button className={classes.button} size="small" color="primary" variant="contained" aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} endIcon={<ExpandMoreIcon />}>
              {percentValue}% of FPL
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {renderSelectValues()}
            </Menu>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <ResponsiveContainer height={220}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                startAngle={0}
                innerRadius={70}
                outerRadius={100}
                label={renderCustomizedLabel}
                labelLine={false}
                fill={primaryColor}
              >
                {pieData.map((entry, index) => (
                  <Cell key={entry} fill={[primaryColor, contrastColor][index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Grid>

      </Grid>
    </>
  );
}

export default PercentOfPriceAndPercentOfMedianIncomePoverty;
